import { action, makeObservable, observable } from 'mobx';

class IntercomContextStore {
  @observable currentCollectionId: string = '';
  @observable currentCollectionName: string = '';
  @observable currentProgramId: string = '';

  private updateTimeout: NodeJS.Timeout | null = null;
  private pendingCollectionUpdate: { id: string; name: string } | null = null;
  private pendingProgramUpdate: string | null = null;

  constructor() {
    makeObservable(this);
  }

  @action setCurrentCollection = (id: string, name: string): void => {
    this.pendingCollectionUpdate = { id, name };
    this.scheduleUpdate();
  };

  @action setCurrentProgram = (programInfo: string): void => {
    this.pendingProgramUpdate = programInfo;
    this.scheduleUpdate();
  };

  @action clearCurrentCollection = (): void => {
    this.pendingCollectionUpdate = { id: '', name: '' };
    this.scheduleUpdate();
  };

  @action clearCurrentProgram = (): void => {
    this.pendingProgramUpdate = '';
    this.scheduleUpdate();
  };

  @action clearAll = (): void => {
    this.pendingCollectionUpdate = { id: '', name: '' };
    this.pendingProgramUpdate = '';
    this.scheduleUpdate();
  };

  private scheduleUpdate = (): void => {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    this.updateTimeout = setTimeout(() => {
      this.applyPendingUpdates();
      this.updateTimeout = null;
    }, 100); // 100ms debounce
  };

  @action private applyPendingUpdates = (): void => {
    if (this.pendingCollectionUpdate) {
      this.currentCollectionId = this.pendingCollectionUpdate.id;
      this.currentCollectionName = this.pendingCollectionUpdate.name;
      this.pendingCollectionUpdate = null;
    }

    if (this.pendingProgramUpdate !== null) {
      this.currentProgramId = this.pendingProgramUpdate;
      this.pendingProgramUpdate = null;
    }
  };

  cleanup = (): void => {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }
    this.pendingCollectionUpdate = null;
    this.pendingProgramUpdate = null;
  };

  get currentCollectionFormatted(): string {
    if (!this.currentCollectionId || !this.currentCollectionName) {
      return '';
    }
    return `${this.currentCollectionId}::${this.currentCollectionName}`;
  }
}

export const intercomContextStore = new IntercomContextStore();
export type { IntercomContextStore };
